import { useState, useEffect } from "react";
import { CheckCircle2, MessageSquare, X } from "lucide-react";
import { usePostHog } from "@posthog/react";
import { cn } from "@/lib/utils";

const SURVEY_ID = "019e1c14-3c89-0000-2db5-7251c3296611";

const ROLES = ["Coach", "Player", "Team management", "Other"];

interface UserFeedbackDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export function UserFeedbackDialog({ isOpen, onClose }: UserFeedbackDialogProps) {
    const posthog = usePostHog();
    const [nps, setNps] = useState<number | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [otherRole, setOtherRole] = useState("");
    const [feedback, setFeedback] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (isOpen) {
            posthog?.capture("survey shown", { $survey_id: SURVEY_ID });
        }
    }, [isOpen, posthog]);

    const handleClose = () => {
        if (!isSuccess) {
            posthog?.capture("survey dismissed", { $survey_id: SURVEY_ID });
        }
        onClose();
        setTimeout(() => {
            setNps(null);
            setRole(null);
            setOtherRole("");
            setFeedback(null);
            setIsSuccess(false);
        }, 200);
    };

    const isRoleValid = () => role !== null && (role !== "Other" || otherRole.trim() !== "");

    const handleSubmit = () => {
        posthog?.capture("survey sent", {
            $survey_id: SURVEY_ID,
            $survey_response: nps,
            $survey_response_1: role === "Other" ? otherRole.trim() : role,
            $survey_response_2: feedback?.trim() ?? null,
        });

        setIsSuccess(true);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-surface-container-lowest border border-outline-variant/20 rounded-md shadow-[0_30px_60px_rgba(5,0,102,0.06)] w-full max-w-lg mx-4 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-outline-variant/20 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <MessageSquare className="w-6 h-6 text-primary" />
                        <h2 className="text-xl font-headline font-bold text-on-surface">Share your feedback</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {isSuccess ? (
                    <>
                        <div className="flex flex-col items-center gap-3 py-10 px-6 text-center">
                            <CheckCircle2 className="w-12 h-12 text-green-500" />
                            <p className="text-base font-headline font-bold text-on-surface">Thank you!</p>
                            <p className="text-sm text-on-surface-variant">Your feedback has been submitted.</p>
                        </div>
                        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-low flex-shrink-0">
                            <div className="flex items-center justify-end">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-outline rounded-md transition-colors cursor-pointer font-headline font-bold uppercase tracking-wider text-xs"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col gap-6 p-6">
                            <div className="flex flex-col gap-3">
                                <label className="text-sm font-medium text-on-surface">
                                    How would you rate your overall experience with the app?
                                </label>
                                <div className="flex gap-1">
                                    {Array.from({ length: 5 }, (_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setNps(i + 1)}
                                            className={cn(
                                                "flex-1 h-9 rounded text-sm font-medium transition-all cursor-pointer",
                                                nps === i + 1
                                                    ? "bg-primary text-white"
                                                    : "bg-surface-container-highest text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
                                            )}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex justify-between text-xs text-on-surface-variant">
                                    <span>Poor</span>
                                    <span>Excellent</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2.5">
                                <label className="text-sm font-medium text-on-surface">
                                    Which role best describes you?
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {ROLES.map((r) => (
                                        <button
                                            key={r}
                                            onClick={() => setRole(r)}
                                            className={cn(
                                                "px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer",
                                                role === r
                                                    ? "bg-primary text-white"
                                                    : "bg-surface-container-highest text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
                                            )}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>
                                {role === "Other" && (
                                    <input
                                        autoFocus
                                        type="text"
                                        value={otherRole}
                                        onChange={(e) => setOtherRole(e.target.value)}
                                        placeholder="Describe your role..."
                                        className="w-full rounded-md bg-surface-container-highest px-4 py-2.5 text-sm text-on-surface transition-all placeholder:text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:bg-surface-container-high"
                                    />
                                )}
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-on-surface">
                                    What can we do to improve the app for you? Share any thoughts, ideas, or feature
                                    requests here.
                                </label>
                                <textarea
                                    value={feedback ?? ""}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="Share your thoughts..."
                                    rows={3}
                                    className="w-full rounded-md bg-surface-container-highest px-4 py-3 text-sm text-on-surface resize-none transition-all placeholder:text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:bg-surface-container-high"
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-low flex-shrink-0">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-outline rounded-md transition-colors cursor-pointer font-headline font-bold uppercase tracking-wider text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!nps && !isRoleValid() && !feedback?.trim()}
                                    className="bg-primary text-white px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
